import { Badge } from '@/components/ui/shadcn/badge'
import { SparklesIcon } from 'lucide-react'
import type { MoodboardBrief, MoodboardBriefBlock } from '@backend/lib/db/schema/moodboard'
import { AddBlockButton, BlockRenderer } from './brief-blocks/block-renderer'

type Props = {
  brief: MoodboardBrief | null
  generatedAt: Date | string | null
  stale: boolean
  // Persists the edited brief through tRPC. Receives the full new brief.
  onBriefChange: (next: MoodboardBrief) => void | Promise<void>
}

export function AnalysisPanel({ brief, generatedAt, stale, onBriefChange }: Props) {
  const hasBrief = !!brief && brief.blocks.length > 0

  const updateBlock = (index: number, next: MoodboardBriefBlock) => {
    if (!brief) return
    void onBriefChange({
      ...brief,
      blocks: brief.blocks.map((b, i) => (i === index ? next : b)),
    })
  }
  const deleteBlock = (index: number) => {
    if (!brief) return
    void onBriefChange({
      ...brief,
      blocks: brief.blocks.filter((_, i) => i !== index),
    })
  }
  const addBlock = (block: MoodboardBriefBlock) => {
    const base = brief ?? { blocks: [] as MoodboardBriefBlock[] }
    void onBriefChange({ ...base, blocks: [...base.blocks, block] })
  }

  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        <SparklesIcon size={16} className="text-accent" />
        <h2 className="text-base font-semibold">Analysis brief</h2>
        {hasBrief && stale && (
          <Badge variant="outline" className="text-[10px] uppercase">
            Stale
          </Badge>
        )}
        {hasBrief && !stale && (
          <Badge variant="secondary" className="text-[10px] uppercase">
            Up to date
          </Badge>
        )}
      </div>

      {hasBrief ? (
        <div className="space-y-6">
          {brief.blocks.map((block, i) => (
            <BlockRenderer
              key={i}
              block={block}
              onChange={(next) => updateBlock(i, next)}
              onDelete={() => deleteBlock(i)}
            />
          ))}
          <div className="border-border/60 border-t pt-3">
            <AddBlockButton onAdd={addBlock} />
          </div>
          {generatedAt && (
            <p className="text-muted-foreground text-[11px]">
              Generated {new Date(generatedAt).toLocaleString()}
            </p>
          )}
        </div>
      ) : (
        <div className="border-border/60 rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">
            Add items, then click <span className="font-medium">Analyze</span> in the page header to
            generate the writing brief.
          </p>
        </div>
      )}
    </section>
  )
}
